import { computed, ref, toValue, watch } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';

import customFormsService from '@satellite/services/custom-forms-service.js';
import { DEFAULT_INVALIDATE_OPTIONS } from '@satellite/composables/yard/yardQueryKeys';

import type { MaybeRefOrGetter } from 'vue';

type SearchParams = Record<string, unknown>;

export interface UseAssetContainerTriggersByWarehouseOptions {
  warehouseId: MaybeRefOrGetter<string | null | undefined>;
  searchParams?: MaybeRefOrGetter<SearchParams>;
  headless?: boolean;
  mode?: 'live' | 'lazy-live';
  staleTime?: number;
}

/**
 * Warehouse-scoped asset container detail triggers for custom forms (read-only list use cases).
 * Deduped per warehouse + searchParams via TanStack Query and {@link customFormsService.getAssetContainerTriggersV2}.
 */
export function useAssetContainerTriggersByWarehouse(
  options: UseAssetContainerTriggersByWarehouseOptions
) {
  const { warehouseId, headless = false, mode = 'live', staleTime = Infinity } = options;
  const searchParamsInput: MaybeRefOrGetter<SearchParams> =
    options.searchParams ?? (() => ({ isActive: true }));
  const warehouseIdComputed = computed(() => toValue(warehouseId) ?? null);
  const searchParamsComputed = computed(() => toValue(searchParamsInput) ?? {});
  const queryClient = useQueryClient();
  const lazyLiveUnlocked = ref(mode !== 'lazy-live');
  const isInViewport = ref(headless);
  const pendingInvalidation = ref(false);
  const queryKey = computed(() => [
    'asset-container-triggers',
    warehouseIdComputed.value,
    JSON.stringify(searchParamsComputed.value)
  ]);

  const wakeLazyLive = () => {
    if (mode === 'lazy-live' && !lazyLiveUnlocked.value) {
      lazyLiveUnlocked.value = true;
    }
  };

  const query = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      const wid = warehouseIdComputed.value;
      if (!wid) {
        return [];
      }

      return customFormsService.getAssetContainerTriggersV2({
        warehouseId: wid,
        searchParams: searchParamsComputed.value,
        requestConfig: { signal },
        enableCache: true
      });
    },
    enabled: computed(
      () =>
        (mode === 'live' || lazyLiveUnlocked.value) &&
        Boolean(warehouseIdComputed.value) &&
        Boolean(isInViewport.value)
    ),
    staleTime,
    refetchOnWindowFocus: false
  });

  const invalidate = () => {
    wakeLazyLive();
    if (!isInViewport.value) {
      pendingInvalidation.value = true;
      return;
    }
    queryClient.invalidateQueries(
      {
        queryKey: queryKey.value
      },
      DEFAULT_INVALIDATE_OPTIONS
    );
  };

  const refresh = async () => {
    wakeLazyLive();
    await query.refetch();
  };

  const onIntersect = (entry?: { isIntersecting?: boolean } | boolean) => {
    if (headless) {
      return;
    }
    const isIntersecting = typeof entry === 'boolean' ? entry : entry?.isIntersecting ?? true;
    isInViewport.value = isIntersecting;

    if (isIntersecting && pendingInvalidation.value) {
      pendingInvalidation.value = false;
      invalidate();
    }
  };

  watch([warehouseIdComputed, searchParamsComputed], () => {
    pendingInvalidation.value = false;

    if (mode === 'lazy-live') {
      lazyLiveUnlocked.value = false;
    }
  });

  return {
    data: computed(() => (Array.isArray(query.data.value) ? query.data.value : [])),
    isLoading: computed(() => query.isLoading.value),
    isFetching: computed(() => query.isFetching.value),
    error: computed(() => query.error.value),
    refresh,
    invalidate,
    onIntersect,
    query
  };
}
